import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import {
  getOnboardingStatus,
  type OnboardingStatus,
  type OnboardingStep,
} from "../lib/onboarding";
import { navigate } from "../lib/router";

const DISMISSED_KEY = "dp-onboarding-dismissed";

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}
function setDismissed(v: boolean): void {
  try {
    if (v) localStorage.setItem(DISMISSED_KEY, "1");
    else localStorage.removeItem(DISMISSED_KEY);
  } catch {
    /* noop */
  }
}

export function OnboardingChecklist() {
  const { data } = useQuery<OnboardingStatus>({
    queryKey: ["agent-onboarding"],
    queryFn: () => getOnboardingStatus(),
    refetchInterval: 60_000,
  });

  // Default zugeklappt — User klickt aktiv um zu sehen was zu tun ist
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(isDismissed);

  if (!data || hidden) return null;
  // Sobald komplett — Karte ausblenden + permanent dismissen
  if (data.all_done) {
    if (!isDismissed()) {
      // bleibt 1x sichtbar als Belohnung
      return <CompletedCard onClose={() => { setDismissed(true); setHidden(true); }} />;
    }
    return null;
  }

  return (
    <div className="mb-4 rounded-xl border border-line bg-white/60 backdrop-blur-sm overflow-hidden">
      {/* Header — dezent, kompakt */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-cream-soft/40 transition"
      >
        <div className="grid size-6 place-items-center rounded-md bg-cream-soft text-accent-orange flex-shrink-0">
          <Sparkles className="size-3.5" />
        </div>
        <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-ink-soft">Schnellstart</span>
          <span className="text-[11px] font-mono text-ink-muted">
            {data.done_count} / {data.total_count}
          </span>
          <div className="flex-1 min-w-[60px] max-w-[140px] h-1 bg-cream-soft rounded-full overflow-hidden">
            <div
              className="h-full brand-gradient transition-all"
              style={{ width: `${data.percent}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-ink-muted">{data.percent}%</span>
        </div>
        {expanded ? (
          <ChevronUp className="size-3.5 text-ink-muted flex-shrink-0" />
        ) : (
          <ChevronDown className="size-3.5 text-ink-muted flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-line">
          {data.steps.map((s, i) => (
            <StepRow key={s.id} step={s} isLast={i === data.steps.length - 1} />
          ))}
          <div className="px-4 py-2 text-right border-t border-line/60">
            <button
              onClick={() => {
                setDismissed(true);
                setHidden(true);
              }}
              className="text-[11px] text-ink-muted hover:text-ink"
            >
              Mach mich weg
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepRow({ step, isLast }: { step: OnboardingStep; isLast: boolean }) {
  const hasProgress = step.target > 1;
  const pct = hasProgress
    ? Math.min(100, Math.round((step.current / step.target) * 100))
    : step.done
      ? 100
      : 0;

  return (
    <div
      className={`px-4 py-3 flex items-center gap-3 ${
        isLast ? "" : "border-b border-line/60"
      } ${step.done ? "" : "hover:bg-cream-soft/30"}`}
    >
      <div
        className={
          step.done
            ? "grid size-7 place-items-center rounded-full bg-emerald-500 text-white flex-shrink-0"
            : "size-7 rounded-full border-2 border-line bg-white flex-shrink-0"
        }
      >
        {step.done && <Check className="size-4" strokeWidth={3} />}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={
            step.done
              ? "text-sm font-medium line-through text-ink-muted"
              : "text-sm font-medium"
          }
        >
          {step.title}
        </div>
        {!step.done && (
          <p className="text-xs text-ink-muted mt-0.5">{step.description}</p>
        )}
        {hasProgress && !step.done && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 h-1 bg-cream-soft rounded-full overflow-hidden">
              <div
                className="h-full brand-gradient transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-ink-muted">
              {step.current} / {step.target}
            </span>
          </div>
        )}
      </div>
      {!step.done && step.action_path && step.action_label && (
        <button
          onClick={() => navigate(step.action_path!)}
          className="inline-flex items-center gap-1 rounded-lg brand-gradient text-white px-3 py-1.5 text-xs font-medium flex-shrink-0 hover:brightness-110"
        >
          {step.action_label}
          <ArrowRight className="size-3" />
        </button>
      )}
    </div>
  );
}

function CompletedCard({ onClose }: { onClose: () => void }) {
  return (
    <div className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-4 py-4 flex items-center gap-3">
      <div className="grid size-12 place-items-center rounded-full bg-emerald-500 text-white text-2xl flex-shrink-0">
        🎉
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-emerald-900">Alles eingerichtet!</div>
        <p className="text-sm text-emerald-800 mt-0.5">
          Du hast alle Schnellstart-Schritte abgeschlossen. Jetzt geht's ans Verdienen.
        </p>
      </div>
      <button
        onClick={onClose}
        className="text-xs text-emerald-700 hover:text-emerald-900 underline flex-shrink-0"
      >
        OK
      </button>
    </div>
  );
}
